import { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import ExpanderIcon from "@components/Svg/ExpanderIcon";
import Logo from "@components/UI/Logo/Logo";
import useIsMobile from "@src/hooks/useIsMobile";
import { WhiteColor } from "@app/styles/color-palette";
import { CURRENT_YEAR } from "@shared/config/Date";
import { SLOGAN_ADDITIONAL_TEXT, SLOGAN_TEXT } from "@shared/config/GlobalText";
import {
    CONTACTS,
    COPYRIGHT_NOTICE,
    DOCUMENTS,
    ExpandState,
    MEDIA,
    NAVIGATION,
    SOCIALS
} from "./constants";
import classes from "./footer.module.scss";

const Footer = () => {
    const isMobile = useIsMobile();

    const expandButtonProps = {
        color: WhiteColor,
        circleColor: "var(--color-background)"
    };

    const [expandedState, setExpandedState] = useState<ExpandState>({
        navigation: true,
        contacts: true,
        media: true
    });

    const toggleExpand = (key: keyof ExpandState) => {
        setExpandedState((prevState) => ({
            ...prevState,
            [key]: !prevState[key]
        }));
    };

    const handleExpandButtonClick = (key: keyof ExpandState) => {
        if (!isMobile) {
            return;
        }
        return (event: React.MouseEvent<HTMLElement>) => {
            event.stopPropagation();
            toggleExpand(key);
        };
    };

    useEffect(() => {
        if (!isMobile) {
            setExpandedState({
                navigation: true,
                contacts: true,
                media: true
            });
        }
    }, [isMobile]);

    return (
        <footer className={classes.footer}>
            <div className={classes.footer__top}>
                <div className={classes.footer__wrapper}>
                    <div className={classes.footer__inner}>
                        <div className={classes.footer__logo}>
                            <Logo width={90} height={50} />
                            <div className={classes.footer__slogan}>
                                {SLOGAN_TEXT} {SLOGAN_ADDITIONAL_TEXT}
                            </div>
                        </div>

                        <div className={classes.footer__column}>
                            <label
                                onClick={handleExpandButtonClick("navigation")}
                                className={classes.footer__label}
                            >
                                {NAVIGATION.LABEL}
                                <span className={classes.ExpandIcon}>
                                    <ExpanderIcon
                                        expanded={expandedState.navigation}
                                        {...expandButtonProps}
                                    />
                                </span>
                            </label>
                            {expandedState.navigation && (
                                <ul className={classes.footer__list}>
                                    {NAVIGATION.ITEMS.map((item, index) => (
                                        <li key={index}>
                                            <Link href={item.LINK} aria-label={item.ARIA_LABEL}>
                                                {item.TEXT}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        <div className={classes.footer__column}>
                            <label
                                onClick={handleExpandButtonClick("contacts")}
                                className={classes.footer__label}
                            >
                                {CONTACTS.LABEL}
                                <span className={classes.ExpandIcon}>
                                    <ExpanderIcon
                                        expanded={expandedState.contacts}
                                        {...expandButtonProps}
                                    />
                                </span>
                            </label>
                            {expandedState.contacts && (
                                <ul className={classes.footer__list}>
                                    <li>
                                        <Link
                                            href={"/contacts"}
                                            rel="noopener noreferrer"
                                            aria-label={CONTACTS.ITEMS[0].ARIA_LABEL}
                                        >
                                            Общие контакты
                                        </Link>
                                    </li>
                                </ul>
                            )}
                        </div>

                        <div className={classes.footer__column}>
                            <label
                                onClick={handleExpandButtonClick("media")}
                                className={classes.footer__label}
                            >
                                {MEDIA.LABEL}
                                <span className={classes.ExpandIcon}>
                                    <ExpanderIcon
                                        expanded={expandedState.media}
                                        {...expandButtonProps}
                                    />
                                </span>
                            </label>
                            {expandedState.media && (
                                <ul
                                    className={classes.footer__list}
                                    style={{
                                        gap: 0
                                    }}
                                >
                                    <li>
                                        <Link
                                            href={MEDIA.ITEMS[0].LINK}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {MEDIA.ITEMS[0].TEXT}
                                        </Link>
                                    </li>
                                    <li>
                                        <div className={classes.footer__socials}>
                                            {SOCIALS.map((social, index) => (
                                                <Link
                                                    key={index}
                                                    href={social.href}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    aria-label={social.ariaLabel}
                                                    className={classes["footer__socials--link"]}
                                                >
                                                    <social.icon
                                                        className={classes["footer__icon"]}
                                                    />
                                                </Link>
                                            ))}
                                        </div>
                                    </li>
                                    {}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>

                <div className={classes.DownloadApp}>
                    <label>Загрузите приложение LIKVI</label>
                    <div className={classes.DownloadApp__links}>
                        <a
                            href="https://appgallery.huawei.com/app/C113420469"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Скачать приложение LIKVI на Android"
                        >
                            <Image width={99} height={37} src="/mobile/apk.svg" alt="Google Play" />
                        </a>
                        <a
                            href="https://apps.apple.com/tr/app/likvi/id6498891460"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Скачать приложение LIKVI на iOS"
                        >
                            <Image
                                width={99}
                                height={40.5}
                                src="/mobile/appstore.svg"
                                alt="App Store"
                            />
                        </a>
                        <a
                            href="https://appgallery.huawei.com/app/C113420469"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Скачать приложение LIKVI на app gallery"
                        >
                            <Image
                                width={128}
                                height={40.5}
                                src="/mobile/appgallery.svg"
                                alt="App Gallery"
                            />
                        </a>
                    </div>
                </div>
            </div>
            <div className={classes.footer__wrapper}>
                <div className={classes.footer__bottom}>
                    <span className={classes.footer__copyright}>
                        {CURRENT_YEAR} {COPYRIGHT_NOTICE}
                    </span>
                    <div className={classes.footer__payment_systems}>
                        <ul className={classes.footer__list}>
                            <li>
                                <Image
                                    width={25}
                                    height={24}
                                    src="/img/payment-systems/mir.svg"
                                    alt="MIR"
                                />
                            </li>
                            <li>
                                <Image
                                    width={24}
                                    height={24}
                                    src="/img/payment-systems/visa.svg"
                                    alt="visa"
                                />
                            </li>
                            <li>
                                <Image
                                    width={25}
                                    height={24}
                                    src="/img/payment-systems/visa2.svg"
                                    alt="visa"
                                />
                            </li>
                            <li>
                                <Image
                                    width={25}
                                    height={24}
                                    src="/img/payment-systems/master.svg"
                                    alt="master"
                                />
                            </li>
                            <li>
                                <Image
                                    width={25}
                                    height={24}
                                    src="/img/payment-systems/sbp.svg"
                                    alt="sbp"
                                />
                            </li>
                        </ul>
                    </div>
                    <div className={classes.footer__documents}>
                        <ul className={classes.footer__list}>
                            {DOCUMENTS.ITEMS.map((item, index) => (
                                <li key={index}>
                                    <Link
                                        href={item.LINK}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label={item.ARIA_LABEL}
                                    >
                                        {item.TEXT}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;